import type { CartItem } from '../types';
import { sumTicketLinesCents } from './tierCartPricing';

/**
 * Aligns with app `buildPaymentIntentDiscountMetadata` + `IProcessPayment.metadata`:
 * `original_total_cents` / `discounted_total_cents` / `discount_savings_cents` / `discount_id` / `discount_code`.
 * `lineSubtotalCents` = sum of **discounted** line items in cents (tickets + tables), before any platform fee
 *   embedded in `orderTotalCents` vs that subtotal.
 * `tableListCents` = list/face value of table lines in cents (usually same as their line subtotal; no sponsor on tables).
 * `orderTotalCents` = final charge (e.g. `totalWithFees` from API).
 */
export function buildSponsorOrderMetadataFromCart(
  cart: CartItem[],
  sponsorUnlocks: Record<string, { value: number; type: string; code: string }> | undefined,
  lineSubtotalCents: number,
  orderTotalCents: number,
  tableListCents: number = 0,
): Record<string, string | number> {
  if (!sponsorUnlocks || Object.keys(sponsorUnlocks).length === 0) {
    return {};
  }

  const listTicketCents = sumTicketLinesCents(cart, {});
  const extraOverLinesCents = Math.max(0, orderTotalCents - lineSubtotalCents);
  const originalOrderCents = listTicketCents + tableListCents + extraOverLinesCents;
  const savingsCents = Math.max(0, originalOrderCents - orderTotalCents);

  let discountId: string | undefined;
  let discountCode: string | undefined;
  for (const c of cart) {
    if (!sponsorUnlocks[c.tier._id]) continue;
    if (!discountId && c.tier.sponsor_discount?._id) {
      discountId = c.tier.sponsor_discount._id;
    }
    if (!discountCode) {
      const u = sponsorUnlocks[c.tier._id];
      if (u?.code) discountCode = u.code;
    }
  }

  return {
    original_total_cents: originalOrderCents,
    discounted_total_cents: orderTotalCents,
    discount_savings_cents: savingsCents,
    ...(discountId && { discount_id: discountId }),
    ...(discountCode && { discount_code: discountCode }),
  };
}

/**
 * `discount: { is_discounted, code }` — same shape as app checkout metadata when a code is applied.
 */
export function buildSponsorDiscountObjectForMetadata(
  sponsorUnlocks: Record<string, { value: number; type: string; code: string }> | undefined,
): { is_discounted: true; code: string } | undefined {
  if (!sponsorUnlocks || Object.keys(sponsorUnlocks).length === 0) return undefined;
  for (const c of Object.values(sponsorUnlocks)) {
    if (c?.code) {
      return { is_discounted: true, code: c.code };
    }
  }
  return { is_discounted: true, code: '' };
}
